import { useEffect } from 'react';

import { atom, bridgeAtom, messageTextAtom, sessionFinishTimeAtom, store, useAtom, useValue } from '@/store';

import { getCookie, setCookie } from '@/modules/common';
import { SESSION_FINISH_TIME_STORAGE } from '@/store/constants';

import { SESSION_LIFETIME } from './constants';

import type { Getter, Setter } from '@/store';

const setTimeCookie = (time: number) => {
  const { isSupportSubDomainWithCookie } = store.get(bridgeAtom);

  if (isSupportSubDomainWithCookie) {
    setCookie({
      days: 7,
      value: String(time),
      name: SESSION_FINISH_TIME_STORAGE,
      useRootDomain: true,
    });
  }
};

const setter = (_: Getter, set: Setter, time?: number) => {
  if (!time) {
    const newTime = Date.now() + SESSION_LIFETIME;

    set(sessionFinishTimeAtom, newTime);
    setTimeCookie(newTime);
    return true;
  }

  const timeElapsed = Date.now() - time;

  if (timeElapsed >= SESSION_LIFETIME) {
    set(sessionFinishTimeAtom, 0);
    setTimeCookie(0);
    return false;
  }

  set(sessionFinishTimeAtom, time);
  setTimeCookie(time);

  return true;
};

const getter = (get: Getter) => {
  let finishTime = get(sessionFinishTimeAtom);

  if (!finishTime) {
    const cookieTime = Number(getCookie(SESSION_FINISH_TIME_STORAGE));

    if (cookieTime) {
      finishTime = cookieTime;
    }
  }

  const timeElapsed = Date.now() - finishTime;

  if (timeElapsed >= SESSION_LIFETIME) {
    return 0;
  }

  return finishTime;
};

export const timeAtom = atom(getter, setter);

/**
 * if session is expired, it will return 0.
 *
 * if time is not provided, it will set the finish time to the current time + SESSION_LIFETIME
 * @returns (time?: number) => boolean
 */

export const useSessionFinishTime = (callback?: () => void): ((time?: number) => boolean) => {
  const [time, setTime] = useAtom(timeAtom);

  const messageText = useValue(messageTextAtom);

  useEffect(() => {
    // give user time to finish message typing
    if (messageText) {
      setTime();
    }
  }, [messageText]);

  // schedule the callback when a session is finished
  useEffect(() => {
    const timeDifference = time - Date.now();

    if (timeDifference > 0 && callback) {
      const timeoutId = setTimeout(() => {
        callback?.();
      }, timeDifference);

      // Cleanup the timeout when the component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [time]);

  return setTime;
};
